import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Truck, Factory, Utensils, HardHat, Flame, Car, Wind, Warehouse } from 'lucide-react';

const MANUFACTURING_IMG = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/xmkdi5p0_IMG_0874.jpg";
const FLEET_IMG = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/3k8rtbma_IMG_0935.PNG";
const WAREHOUSE_IMG = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/fph3vzj9_IMG_0924.PNG";

const industries = [
  { 
    id: 1, 
    name: 'Military Bases', 
    desc: 'Equipment & facility cleaning for government contracts. We support military operations with rapid-response cleaning services that meet DoD standards.',
    icon: Shield,
    features: ['DoD-compliant processes', 'Security-cleared personnel', 'Mobile deployment capable']
  },
  { 
    id: 2, 
    name: 'Government Fleets', 
    desc: 'Municipal vehicle maintenance facilities. Keep city, county, and state fleets running efficiently with our waterless cleaning solutions.',
    icon: Truck,
    features: ['GSA Schedule eligible', 'Minimal facility disruption', 'Environmentally compliant']
  },
  { 
    id: 3, 
    name: 'Public Works', 
    desc: 'Infrastructure and equipment maintenance for public sector operations.',
    icon: HardHat,
    features: ['Heavy equipment cleaning', 'Utility infrastructure', 'Road maintenance equipment']
  },
  { 
    id: 4, 
    name: 'Manufacturing Plants', 
    desc: 'Production line and machinery cleaning without interrupting operations.',
    icon: Factory,
    features: ['In-place cleaning', 'Zero water contamination', 'Reduced production downtime']
  },
  { 
    id: 5, 
    name: 'Food Processing', 
    desc: 'FDA/USDA-compliant sanitation support for food-grade facilities.',
    icon: Utensils,
    features: ['FDA/USDA compliant', 'No chemical residue', 'Food-safe process']
  },
  { 
    id: 6, 
    name: 'Heavy Equipment', 
    desc: 'Construction, oil & gas equipment cleaning and restoration.',
    icon: HardHat,
    features: ['Field service available', 'Hydraulic system safe', 'Extends equipment life']
  },
  { 
    id: 7, 
    name: 'Fleet Operations', 
    desc: 'Trucking, logistics, and transportation fleet maintenance.',
    icon: Truck,
    features: ['Engine bay degreasing', 'Undercarriage cleaning', 'DOT inspection ready']
  },
  { 
    id: 8, 
    name: 'Fire Restoration', 
    desc: 'Post-fire soot and smoke damage cleanup for rapid recovery.',
    icon: Flame,
    features: ['Soot & char removal', 'Smoke odor elimination', 'Salvage operations']
  },
  { 
    id: 9, 
    name: 'Auto Dealerships', 
    desc: 'Detail-level engine bay and undercarriage cleaning for sales prep.',
    icon: Car,
    features: ['Pre-sale detailing', 'Trade-in restoration', 'Show-ready results']
  },
  { 
    id: 10, 
    name: 'HVAC Contractors', 
    desc: 'Commercial duct and system cleaning for improved performance.',
    icon: Wind,
    features: ['Coil cleaning', 'Ductwork restoration', 'Energy efficiency boost']
  },
  { 
    id: 11, 
    name: 'Warehouses', 
    desc: 'Floor, dock, and structural cleaning for distribution centers.',
    icon: Warehouse,
    features: ['Loading dock cleaning', 'Structural maintenance', '24/7 service available']
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export const IndustriesPage = () => {
  return (
    <main className="bg-black pt-20" data-testid="industries-page">
      {/* Hero Section */}
      <section className="py-24 bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="badge bg-orange-500/10 border border-orange-500/50 text-orange-500 px-4 py-2 mb-6 inline-block">
              Target Markets
            </span>
            <h1 className="text-5xl sm:text-6xl md:text-7xl uppercase tracking-wider font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Industries We Serve
            </h1>
            <p className="text-zinc-300 text-lg leading-relaxed">
              From military installations to manufacturing plants, we deliver specialized dry ice 
              blasting solutions across diverse industries with unique cleaning challenges.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Industries Grid */}
      <section className="py-16 bg-black" data-testid="featured-industries">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
            {[
              { name: 'Military & Government', img: MANUFACTURING_IMG, desc: 'DoD-compliant cleaning solutions' },
              { name: 'Fleet & Transportation', img: FLEET_IMG, desc: 'Keep your fleet running clean' },
              { name: 'Industrial & Manufacturing', img: WAREHOUSE_IMG, desc: 'Production-ready cleanliness' },
            ].map((featured, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.15 }}
                className="industry-card aspect-[4/3] relative overflow-hidden group"
              >
                <img 
                  src={featured.img} 
                  alt={featured.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                <div className="industry-card-content absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl uppercase tracking-wide font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    {featured.name}
                  </h3>
                  <p className="text-zinc-400 text-sm">{featured.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* All Industries */}
      <section className="py-16 bg-zinc-950" data-testid="all-industries">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl uppercase tracking-wide font-bold text-white mb-12" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            All Industries
          </h2>
          
          <motion.div 
            className="grid md:grid-cols-2 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              visible: { transition: { staggerChildren: 0.05 } }
            }}
          >
            {industries.map((industry) => (
              <motion.div
                key={industry.id}
                variants={fadeInUp}
                className="service-card bg-black border border-zinc-800 p-8 group"
                data-testid={`industry-card-${industry.id}`}
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-zinc-900 border border-zinc-800 flex items-center justify-center group-hover:border-orange-500 transition-colors">
                      <industry.icon className="w-7 h-7 text-orange-500" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl uppercase tracking-wide font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      {industry.name}
                    </h3>
                    <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                      {industry.desc}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {industry.features.map((feature, i) => (
                        <span key={i} className="badge bg-zinc-900 border border-zinc-800 text-zinc-500 px-3 py-1 text-xs">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Table */}
      <section className="py-24 bg-black border-t border-zinc-800" data-testid="benefits-table">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Operational Benefits
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              For decision-makers evaluating cleaning solutions
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="benefits-data-table">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-4 px-6 text-zinc-400 font-mono text-xs uppercase tracking-widest">Benefit</th>
                  <th className="text-left py-4 px-6 text-zinc-400 font-mono text-xs uppercase tracking-widest">Detail</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { benefit: 'Downtime', detail: 'Reduced — No disassembly required' },
                  { benefit: 'Cleanup', detail: 'No secondary waste or media to dispose of' },
                  { benefit: 'Safety', detail: 'Non-conductive, no water exposure, no chemicals' },
                  { benefit: 'Maintenance', detail: 'Improved inspection visibility after cleaning' },
                  { benefit: 'Environment', detail: 'EPA-friendly — CO2 is recycled, no hazardous runoff' },
                  { benefit: 'Cost', detail: 'Lower total cost vs. traditional methods' },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-zinc-900 hover:bg-zinc-950 transition-colors">
                    <td className="py-4 px-6 text-orange-500 font-bold uppercase text-sm" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{row.benefit}</td>
                    <td className="py-4 px-6 text-zinc-300">{row.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-black mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Don't See Your Industry?
          </h2>
          <p className="text-black/80 text-lg mb-8 max-w-2xl mx-auto">
            Our dry ice blasting technology adapts to virtually any industrial cleaning challenge. 
            Contact us to discuss your specific needs.
          </p>
          <Link to="/contact" className="bg-black text-white px-10 py-4 text-lg uppercase tracking-widest inline-flex items-center gap-2 hover:bg-zinc-900 transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="industries-cta">
            Contact Us
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
};
